import { SimpleGrid } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useFeatureUsage } from '@/components/subscriptions/useFeatureUsage'
import { useUsageLimitModal } from '@/components/subscriptions/useUsageLimitModal'
import { AddButtonCard } from '@/components/ui-shared/AddButtonCard/AddButtonCard'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetAppMLModels } from '@/queries/modelQueries'
import { ApplicationType } from '@/types/app'
import { MLModel, MLModelType } from '@/types/model'
import { CrudAction, Resource } from '@/types/permissions'
import { ErrorWithReload } from '../../ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '../../ui-shared/InfiniteScroll/InfiniteScroll'
import { CreateModelModal } from '../CreateModelModal/CreateModelModal'
import { DeleteModelModal } from '../DeleteModelModal'
import { ModelCard } from '../ModelCard/ModelCard'
import { UpdateModelModal } from '../UpdateModelModal/UpdateModelModal'

type ModelListProps = {
  applicationId: string
  applicationType: ApplicationType
  modelType: MLModelType
  addModelLabel: string | ReactNode
}

export const ModelList = ({
  applicationId,
  applicationType,
  modelType,
  addModelLabel
}: ModelListProps) => {
  const [
    isCreateModelModalOpen,
    { open: openCreateModelModal, close: closeCreateModelModal }
  ] = useDisclosure(false)

  const [modelToDelete, setModelToDelete] = useState<MLModel | null>(null)
  const [modelToRename, setModelToRename] = useState<MLModel | null>(null)

  const [canCreateModel, canUpdateModel, canDeleteModel] = useHasPermissions(
    Resource.MlModels,
    [CrudAction.Create, CrudAction.Update, CrudAction.Delete]
  )

  const { openUsageLimitModal } = useUsageLimitModal()
  const { usage, refreshFeatureUsage } = useFeatureUsage('user_ml_models')
  const hasRemainingModels = !usage || usage.remaining > 0

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetAppMLModels(applicationId, {
      model_type: modelType
    })

  const models = data?.pages?.flatMap((page) => page.results) || []

  const handleCreateModelClick = () => {
    if (hasRemainingModels) {
      void openCreateModelModal()
    } else {
      openUsageLimitModal('user_ml_models', {
        maxUserModels: usage?.limit
      })
    }
  }

  const handleModelUpdated = () => {
    setModelToRename(null)
    void refetch()
  }

  const handleModelDeleted = () => {
    setModelToDelete(null)
    void refetch()
    void refreshFeatureUsage()
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="models.modelList.apiError" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <InfiniteScroll
        hasMore={hasNextPage}
        isFetching={isFetching}
        onLoadMore={() => void fetchNextPage()}
      >
        <SimpleGrid cols={{ base: 1, md: 2, xl: 4 }} spacing="xl">
          {canCreateModel && (
            <AddButtonCard
              height={280}
              label={addModelLabel}
              onClick={handleCreateModelClick}
            />
          )}

          {models.map((model) => (
            <ModelCard
              key={model.id}
              model={model}
              applicationId={applicationId}
              applicationType={applicationType}
              showActions={canUpdateModel || canDeleteModel}
              showEdit={canUpdateModel}
              showDelete={canDeleteModel}
              onRename={() => setModelToRename(model)}
              onDelete={() => setModelToDelete(model)}
            />
          ))}
        </SimpleGrid>
      </InfiniteScroll>

      <CreateModelModal
        appId={applicationId}
        modelType={modelType}
        opened={isCreateModelModalOpen}
        onClose={closeCreateModelModal}
      />

      {modelToRename && (
        <UpdateModelModal
          opened={modelToRename !== null}
          model={modelToRename}
          onClose={() => setModelToRename(null)}
          onUpdated={handleModelUpdated}
        />
      )}

      <DeleteModelModal
        opened={modelToDelete !== null}
        modelId={modelToDelete?.id}
        modelName={modelToDelete?.name}
        onClose={() => setModelToDelete(null)}
        onDeleted={handleModelDeleted}
      />
    </>
  )
}
